import axios from 'axios'

/**
 * How the backend says "this is about your place at that organisation, not about this request".
 *
 * A 403 with `errorCode: "EMPLOYMENT_REVOKED"` means the caller's employment at the account named by
 * the request has ended. It is not a missing permission and nothing here can be retried into
 * working, so the web must not fall back to the generic "you do not have permission" text: a plain
 * `FORBIDDEN` is a role problem, this is a person who has just been told they no longer work
 * somewhere. The wording says what is true (access has ended), says who to ask, and says nothing
 * about why: the app does not know, and has no business guessing.
 *
 * It is a single problem object (`{ message, errorCode, metadata }`), like the billing refusals.
 */

export const EMPLOYMENT_REVOKED_CODE = 'EMPLOYMENT_REVOKED'
export const EMPLOYMENT_SUSPENDED_CODE = 'EMPLOYMENT_SUSPENDED'

/** Used where the request does not say which organisation (an unpinned or already-forgotten account). */
const THIS_ORGANIZATION = 'this organization'

/** Whether a body decoded separately (for example a download's Blob error) is this refusal. */
export function employmentRevokedFromBody(status: number, data: unknown): boolean {
  if (status !== 403 || !data || typeof data !== 'object' || Array.isArray(data)) return false
  return (data as { errorCode?: unknown }).errorCode === EMPLOYMENT_REVOKED_CODE
}

export function employmentSuspendedFromBody(status: number, data: unknown): boolean {
  if (status !== 403 || !data || typeof data !== 'object' || Array.isArray(data)) return false
  return (data as { errorCode?: unknown }).errorCode === EMPLOYMENT_SUSPENDED_CODE
}

export function isEmploymentSuspended(error: unknown): boolean {
  if (!axios.isAxiosError(error) || !error.response) return false
  return employmentSuspendedFromBody(error.response.status, error.response.data)
}

export function isEmploymentRevoked(error: unknown): boolean {
  if (!axios.isAxiosError(error) || !error.response) return false
  return employmentRevokedFromBody(error.response.status, error.response.data)
}

/**
 * The account the refused request was aimed at. The response carries no metadata, so it is read off
 * the request: the interceptor pins `_accountId` when the request is sent, which is the right
 * answer even if the user has switched organisation by the time the refusal arrives.
 */
export function revokedAccountIdOf(error: unknown): number | null {
  if (!axios.isAxiosError(error)) return null
  const pinned = (error.config as { _accountId?: unknown } | undefined)?._accountId
  if (typeof pinned === 'number' && pinned > 0) return pinned
  const header: unknown = error.config?.headers?.['X-Account-Id']
  const parsed = Number(header)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

export function accessEndedHeadline(accountName: string | null | undefined): string {
  return `Your access to ${accountName || THIS_ORGANIZATION} has ended`
}

/** Who to ask. Deliberately not "why": the app does not know the reason and must not imply one. */
export function accessEndedContact(accountName: string | null | undefined): string {
  return accountName
    ? `If you have questions, contact an administrator at ${accountName}.`
    : 'If you have questions, contact an administrator at that organization.'
}

/** The one-line form, for a toast or an inline error. */
export function employmentRevokedMessage(accountName: string | null | undefined): string {
  return `${accessEndedHeadline(accountName)}. ${accessEndedContact(accountName)}`
}

export function accessPausedHeadline(accountName: string | null | undefined): string {
  return `Your access to ${accountName || THIS_ORGANIZATION} has been paused`
}

export const ACCESS_PAUSED_CONTACT = 'Contact your administrator.'

export function employmentSuspendedMessage(accountName: string | null | undefined): string {
  return `${accessPausedHeadline(accountName)}. ${ACCESS_PAUSED_CONTACT}`
}
